import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";

import messages from "../../../assets/locale/messages";
import { ListDeleteIcon, ListEditIcon } from "../../../utils/Icons";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import ViewDeleteButton from "../../../components/ViewDeleteButton";
import ViewEditButton from "../../../components/ViewEditButton";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import EmptyState from "../../../components/EmptyState";
import TruncateText from "../../../components/TruncateText";
import noCategoryImg from "../../../assets/images/placeholders/noCategoryImagePlaceholder.svg";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import DeleteCategory from "../DeleteCategory";
import {
  getCategoryDetailsRequest,
  getCategoryDetailsResponse,
  categorySubCategoriesListRequest,
  categorySubCategoriesListResponse
} from "../../../store/Categories/actions";
import DeleteSubCategory from "../../SubCategories/DeleteSubCategory";
import { checkForMultiline } from "../../../utils/Helpers";
import { hasPermission, PERMISSIONS_IDS } from "../../../utils/Roles";
import "./CategoryDetails.scss";

const CategoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const categoryDetails = useSelector(
    (state) => state.categories.categoryDetails
  );
  const categorySubCategoriesList = useSelector(
    (state) => state.categories.categorySubCategories.list
  );
  const categorySubCategoriesMeta = useSelector(
    (state) => state.categories.categorySubCategories.meta
  );

  const { categories, shared } = messages[lang];
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [deletedCategoryDetails, setDeletedCategoryDetails] =
    useState({});
  const [deleteCategoryModalOpen, setDeleteCategoryModalOpen] =
    useState(false);
  const [deletedSubCategoryDetails, setDeletedSubCategoryDetails] =
    useState({});
  const [deleteSubCategoryModalOpen, setDeleteSubCategoryModalOpen] =
    useState(false);

  const handleEditCategory = () => {
    navigate(ROUTE_PATHS.editCategory.replace(":id", id));
  };

  const handleDeleteCategory = () => {
    setDeletedCategoryDetails({
      id,
      name: categoryDetails.Name,
      src: ROUTE_PATHS.categoryDetailsApproved
    });
    setDeleteCategoryModalOpen(true);
  };

  const handleDeleteSubCategory = (
    subCategoryId,
    subCategoryName
  ) => {
    setDeletedSubCategoryDetails({
      id: subCategoryId,
      name: subCategoryName,
      src: ROUTE_PATHS.subcategoryDetails
    });
    setDeleteSubCategoryModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteCategoryModalOpen(false);
    setDeletedCategoryDetails({});
    setDeleteSubCategoryModalOpen(false);
    setDeletedSubCategoryDetails({});
  };

  const handleAddSubCategory = () => {
    navigate(ROUTE_PATHS.addSubCategories);
  };

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const requestCategorySubCategoriesList = () => {
    if (id)
      dispatch(
        categorySubCategoriesListRequest({
          params: {
            page,
            search_value: searchValue,
            categoryId: id
          }
        })
      );
  };

  useEffect(() => {
    if (id) {
      dispatch(
        getCategoryDetailsRequest({
          id
        })
      );
    }
  }, [id]);

  useEffect(() => {
    requestCategorySubCategoriesList();
  }, [page, searchValue]);

  useEffect(() => {
    return () => {
      dispatch(getCategoryDetailsResponse({}));
      dispatch(
        categorySubCategoriesListResponse({
          list: [],
          meta: {}
        })
      );
    };
  }, []);

  const renderSubCategoriesTableRow = ({
    Id,
    Name,
    Description,
    AdminCoverImage,
    ProductsCount
  }) => (
    <TableRow
      key={`request-row-${Id}`}
      onClick={() => {
        navigate(ROUTE_PATHS.subcategoryDetails.replace(":id", Id));
      }}>
      <TableCell>
        <div className="d-flex align-items-center gap-3 pointer">
          <img
            src={AdminCoverImage || noRequestImg}
            onError={onImageError}
            alt="subCategoriesImage"
            className="sub-category-img faint-grey-border border-radius-8"
          />
          <TruncateText text={Name || "__"} />
        </div>
      </TableCell>
      <TableCell>
        <div className="w-auto">
          <TruncateText text={Description || "__"} />
        </div>
      </TableCell>
      <TableCell>
        {`${
          ProductsCount ?? "0"
        } ${categories.categoryDetails.tableHeader.products}`}
      </TableCell>
      <TableCell>
        <div className="w-100 d-flex justify-content-center gap-2">
          <ListEditIcon
            className={"list-action-icon pointer"}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                ROUTE_PATHS.editSubCategories.replace(":id", Id)
              );
            }}
          />
          <ListDeleteIcon
            className={"list-delete-action-icon pointer"}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSubCategory(Id, Name);
            }}
          />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="approved-category-details-container d-flex flex-column pb-4">
      <div className="page-title-buttons-container pb-4 d-flex justify-content-between align-items-center">
        <div className="page-title">
          <Typography variant="h5">
            {categories.categoryDetails.categoryDetailsTitle}
          </Typography>
        </div>
        <div className="d-flex gap-3">
          {hasPermission(PERMISSIONS_IDS.category_edit) && (
            <ViewEditButton onClick={handleEditCategory} />
          )}
          {hasPermission(PERMISSIONS_IDS.category_delete) && (
            <ViewDeleteButton onClick={handleDeleteCategory} />
          )}
        </div>
      </div>
      <div className="category-details-container bg-white d-flex w-100 p-4 col-12 gap-3  border-radius-8">
        <div className="img-container col-xl-2 col-lg-3 col-md-3 col-12">
          <img
            src={categoryDetails.AdminCoverImage}
            alt="category image"
            onError={onImageError}
            className="faint-grey-border"
          />
        </div>
        <div className="category-details justify-content-start col-xl-8 col-lg-8 col-md-9 col-12">
          <Typography className="fsize-16 fweight-500 pb-2">
            {categoryDetails.Name}
          </Typography>
          <Typography
            variant="span"
            className="text-secondary category-description">
            {categoryDetails.Description
              ? checkForMultiline(categoryDetails.Description)
              : ""}
          </Typography>
        </div>
      </div>
      <div className="sub-category-list-container">
        <div className="page-title pb-4 d-flex justify-content-between align-items-center mt-3">
          <div className="title-count-container d-flex justify-content-center align-items-center">
            <div>
              <Typography variant="h6">
                {categories.categoryDetails.subCategories}
              </Typography>
            </div>
            <div className="block-title text-secondary ps-1">
              <div className="block-title text-secondary">
                <Typography>{`(${categorySubCategoriesMeta?.count || "0"} ${categorySubCategoriesMeta?.count === 1 ? categories.categoryDetails.subCategory : categories.categoryDetails.subCategories})`}</Typography>
              </div>
            </div>
          </div>
          <div className="title-buttons-container d-flex justify-content-center gap-3 align-items-center">
            {hasPermission(PERMISSIONS_IDS.sub_category_add) && (
              <Button
                outlined
                className={"bg-white"}
                label={
                  <span className="d-flex align-items-center">
                    <Add fontSize="medium" />
                    <span className="ps-1 ">
                      {
                        categories.categoryDetails
                          .addMoreSubCategories
                      }
                    </span>
                  </span>
                }
                onClick={handleAddSubCategory}
              />
            )}
          </div>
        </div>
        <div className="categories-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 border-radius-8">
          <div className="table-header d-flex px-4 align-items-center w-100 mb-4">
            <SearchInput
              name="CategorySearch"
              id="CategorySearch"
              value={searchValue}
              inputClass="border-radius-8"
              inputWrapperClass=" search-input-wrapper search-input-wrapper-width"
              placeholder={
                categories.categoryDetails.searchPlaceholder
              }
              onChange={(value) => {
                setPage(1);
                setSearchValue(value);
              }}
            />
          </div>
          <div className="table-content">
            <div>
              {categorySubCategoriesList?.length ? (
                <Table
                  className={"px-0"}
                  headlines={[
                    categories.categoryDetails.tableHeader
                      .subCategory,
                    categories.categoryDetails.tableHeader
                      .description,
                    categories.categoryDetails.tableHeader.products,
                    shared.actions
                  ]}
                  rows={
                    !isLoading &&
                    categorySubCategoriesList &&
                    categorySubCategoriesList.map((category) =>
                      renderSubCategoriesTableRow(category)
                    )
                  }
                />
              ) : (
                <div className="border-top">
                  {!isLoading && (
                    <EmptyState
                      subTitle={
                        !!searchValue &&
                        !categorySubCategoriesList.length
                          ? categories.list.noSearchResults
                          : categories.categoryDetails
                              .noSubCategoriesFound
                      }
                      showActionButton={
                        !searchValue &&
                        categorySubCategoriesList?.length === 0
                      }
                      ButtonLabel={
                        <span className="d-flex align-items-center">
                          <Add fontSize="medium" />
                          <span className="ps-1 ">
                            {
                              categories.categoryDetails
                                .addMoreSubCategories
                            }
                          </span>
                        </span>
                      }
                      handleCreate={handleAddSubCategory}
                      buttonClassName="empty-state-create-button border-radius-8 w-auto"
                      imgSrc={noCategoryImg}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
            {!isLoading && categorySubCategoriesList?.length > 0 && (
              <Pagination
                count={categorySubCategoriesMeta?.pages}
                page={page || categorySubCategoriesMeta?.currentPage}
                handleChange={(page) => {
                  setPage(page);
                }}
                defaultPage={1}
              />
            )}
          </div>
        </div>
      </div>
      {deleteCategoryModalOpen && (
        <DeleteCategory
          open={deleteCategoryModalOpen}
          closeModal={handleCloseModal}
          categoryInfo={deletedCategoryDetails}
        />
      )}
      {deleteSubCategoryModalOpen && (
        <DeleteSubCategory
          open={deleteSubCategoryModalOpen}
          closeModal={handleCloseModal}
          subCategoryInfo={deletedSubCategoryDetails}
        />
      )}
    </div>
  );
};

export default CategoryDetails;
